/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * JS API
 * https://www.tradingview.com/charting-library-docs/latest/connecting_data/datafeed-api/
 */

import { IMarketPair } from '@/features/market/types';
import { getPriceScale, getSymbolInfo, timesDecimals } from '../utils';

export interface DataFeedOptions {
  SymbolInfo?: TradingView.LibrarySymbolInfo;
  DatafeedConfiguration?: TradingView.DatafeedConfiguration;
  getBars?: TradingView.IDatafeedChartApi['getBars'];
}

export interface DataSubscriber {
  symbolInfo: TradingView.LibrarySymbolInfo;
  resolution: string;
  lastBarTime: number | null;
  listener: TradingView.SubscribeBarsCallback;
}

export class DataFeed implements TradingView.IExternalDatafeed, TradingView.IDatafeedChartApi {
  private options: DataFeedOptions;
  private subscribers: Record<string, DataSubscriber> = {};

  constructor(options: DataFeedOptions) {
    this.options = options;
  }

  /**
   * This method can set the chart configuration supported by the chart library. This data will affect the features supported by the chart.
   * @tips This function will be called back after the chart is successfully initialized, passing the DatafeedConfiguration configuration to the chart through the callback parameter of this function.
   */
  public async onReady(callback: TradingView.OnReadyCallback) {
    return new Promise((resolve) => resolve(void 0)).then(() => {
      if (this.options.DatafeedConfiguration) {
        callback(this.options.DatafeedConfiguration);
      }
    });
  }

  /**
   * Provides a list of products that match the user's search.
   * @tips This is triggered when the search box in the chart header searches for products. You can request your server's products in this function and pass them to the chart through onResult.
   */
  public async searchSymbols(
    userInput: string,
    exchange: string,
    symbolType: string,
    onResult: TradingView.SearchSymbolsCallback,
  ) {
    // If you need to request data from your server, please add relevant code.
    // Example
    // fetch(`./api?userInput=${userInput}`, { method: "GET" }).then(
    //   async (res) => {
    //     const data: TradingView.SearchSymbolResultItem[] = await res.json();
    //     onResult(data); // Please refer to the SearchSymbolResultItem type for the return structure.
    //   }
    // );
    return new Promise((resolve) => resolve(void 0)).then(() => {
      onResult([]);
    });
  }

  /**
   * Resolves product information by product name.
   * @tips This function will be called back after the chart is successfully initialized. You can request your server's product configuration information in this function and pass it to the chart through onSymbolResolvedCallback.
   */
  public async resolveSymbol(
    symbolName: string,
    onSymbolResolvedCallback: TradingView.ResolveCallback,
    onResolveErrorCallback: TradingView.ErrorCallback,
  ) {
    // If you need to request data from your server, please add relevant code. I requested the server during the chart initialization and passed it to the options object.
    // Example
    // fetch(`./api?symbolName=${symbolName}`, { method: 'GET' }).then(async (res) => {
    //   const data: TradingView.LibrarySymbolInfo = await res.json();
    //   onSymbolResolvedCallback({

    //   }); // Please refer to the LibrarySymbolInfo type for the return structure.
    // });

    return new Promise((resolve) => resolve(void 0)).then(() => {
      // const poolAddress = symbolName.split('::')[1];
      const poolAddress = symbolName;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const symbolInfo = getSymbolInfo(poolAddress, (this.options.SymbolInfo as any)?.pool as IMarketPair);

      const digits = getPriceScale(symbolInfo.pool?.fairPrice || '100000', 12);

      symbolInfo.pricescale = timesDecimals(1, digits).toNumber();
      onSymbolResolvedCallback(symbolInfo);
      // if (this.options.SymbolInfo) {
      //   onSymbolResolvedCallback(this.options.SymbolInfo);
      // } else {
      //   onResolveErrorCallback('Failed to get product information');
      // }
    });
  }

  /**
   * This function will be called when the chart library needs historical candlestick segments defined by a date range.
   * @tips This function will be called back after the chart is successfully initialized. You can request your server's historical candlestick data in this function and pass it to the chart through onResult.
   */
  public async getBars(
    symbolInfo: TradingView.LibrarySymbolInfo,
    resolution: TradingView.ResolutionString,
    periodParams: TradingView.PeriodParams,
    onResult: TradingView.HistoryCallback,
    onError: TradingView.ErrorCallback,
  ) {
    if (!this.options.getBars) {
      return new Promise((resolve) => resolve(void 0)).then(() => {
        onResult([]);
      });
    }
    return this.options.getBars(symbolInfo, resolution, periodParams, onResult, onError);
  }

  /**
   * Subscribes to candlestick data. The chart library will call the onRealtimeCallback method to update real-time data.
   * @tips This function will be called back after the historical candlestick is successfully loaded by the chart.
   */
  public subscribeBars(
    symbolInfo: TradingView.LibrarySymbolInfo,
    resolution: string,
    onRealtimeCallback: TradingView.SubscribeBarsCallback,
    subscriberUID: string,
    onResetCacheNeededCallback: () => void,
  ) {
    if (this.subscribers[subscriberUID]) {
      return;
    }
    this.subscribers[subscriberUID] = {
      lastBarTime: null,
      listener: onRealtimeCallback,
      resolution: resolution,
      symbolInfo: symbolInfo,
    };
  }

  /**
   * Unsubscribes from candlestick data.
   */
  public unsubscribeBars(subscriberUID: string) {
    if (!this.subscribers[subscriberUID]) {
      return;
    }
    delete this.subscribers[subscriberUID];
  }

  /**
   * The key function of DataFeed, we pass real-time data to the chart through this function.
   * @tips This function is not a function required by the JS API, but an extension function we created.
   */
  public updateBar(bar: TradingView.Bar) {
    for (const listenerGuid in this.subscribers) {
      const subscriptionRecord = this.subscribers[listenerGuid];
      if (subscriptionRecord.lastBarTime !== null && bar.time < subscriptionRecord.lastBarTime) {
        // If the real-time data time is less than the last candlestick time, skip the update.
        continue;
      }
      subscriptionRecord.lastBarTime = bar.time;
      subscriptionRecord.listener(bar);
    }
  }

  /**
   * The chart library calls this function to obtain the marks of the visible candlestick range.
   * @tips If DatafeedConfiguration enables candlestick marks, the chart will call this function and return the mark data to the chart for rendering through onDataCallback.
   */
  public async getMarks(
    symbolInfo: TradingView.LibrarySymbolInfo,
    startDate: number,
    endDate: number,
    onDataCallback: TradingView.GetMarksCallback<TradingView.Mark>,
    resolution: string,
  ) {
    // If you need to request data from your server, please add relevant code.
    // Example
    // fetch(`./api?symbolName=${symbolName}`, { method: "GET" }).then(
    //   async (res) => {
    //     const data: TradingView.Mark[] = await res.json();
    //     onDataCallback(data);
    //   }
    // );
  }

  /**
   * The chart library calls this function to get the time scale marks of the visible candlestick range.
   * @tips If DatafeedConfiguration enables time scale marks, the chart will call this function and return the mark data to the chart for rendering through onDataCallback.
   */
  public async getTimescaleMarks(
    symbolInfo: TradingView.LibrarySymbolInfo,
    startDate: number,
    endDate: number,
    onDataCallback: TradingView.GetMarksCallback<TradingView.TimescaleMark>,
    resolution: string,
  ) {
    // If you need to request data from your server, please add relevant code.
    // Example
    // fetch(`./api?symbolName=${symbolName}`, { method: "GET" }).then(
    //   async (res) => {
    //     const data: TradingView.TimescaleMark[] = await res.json();
    //     onDataCallback(data);
    //   }
    // );
  }

  /**
   * When the chart needs to know the server time, and the DatafeedConfiguration configuration supports_time is set to true, this function will be called.
   * @tips If DatafeedConfiguration enables server time, the chart will call this function and return the server time to the chart through the callback.
   */
  public getServerTime(callback: TradingView.ServerTimeCallback) {
    // If you need to request data from your server, please add relevant code.
    // Example
    // fetch(`./api?symbolName=${symbolName}`, { method: "GET" }).then(
    //   async (res) => {
    //     const data: TradingView.ServerTimeCallback = await res.json();
    //     callback(data);
    //   }
    // );
  }
}
